import {useMutation, useQuery, useQueryClient, type UseQueryOptions} from '@tanstack/react-query'
import {type FleetConfigKey} from '../store/state/config/state'
import {fetchFleetConfigByKey, upsertFleetConfig} from '../api/fleet-config'

const queryKeys = {
    fleetConfig: 'fleet-config',
    fleetConfigKey: (key: FleetConfigKey) => [queryKeys.fleetConfig, key],
}

export function useFleetConfigKeyQuery(
    key: FleetConfigKey,
    // The generics here is required because
    // we want the return type of this function to be typed,
    // but using spread operator `...options` below makes it
    // not able to infer the type from just returning `useQuery`
    options?: UseQueryOptions<
        // 1st and 3rd parameter can be different if we're using the `select`
        // API in the useQuery option. If you're not using `select`, just specify the 3rd param
        // and let everything else be default
        unknown,
        unknown,
        Awaited<ReturnType<typeof fetchFleetConfigByKey>>
    >,
) {
    return useQuery({
        retry: false,
        queryKey: queryKeys.fleetConfigKey(key),
        queryFn: async () => await fetchFleetConfigByKey(key),
        ...options,
    })
}

export function useFleetConfigKeyUpsert(key: FleetConfigKey) {
    const queryClient = useQueryClient()
    return useMutation({
        retry: false,
        mutationFn: upsertFleetConfig,
        onSuccess: () => {
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-floating-promises, @typescript-eslint/no-deprecated
            queryClient.invalidateQueries(queryKeys.fleetConfigKey(key))
        },
    })
}
